import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../contexts/CartContext';

const Cart = () => {
  const { cartItems, updateQuantity, removeFromCart, getCartTotal, customPrices, getEffectivePrice } = useCart();
  const navigate = useNavigate();

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="h-24 w-24 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Your cart is empty</h2>
          <p className="text-slate-600 mb-6">Add some products to get started</p>
          <Link to="/products">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">Shop Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  const hasAnyContractPrice = cartItems.some(i => customPrices[i.id] !== undefined);

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="cart-page">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold mb-2 text-slate-800">Shopping Cart</h1>
        {hasAnyContractPrice && (
          <p className="text-sm text-emerald-700 mb-6 flex items-center gap-2" data-testid="contract-price-banner">
            <Tag className="h-4 w-4" /> Contract pricing applied to your account.
          </p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map(item => {
              const effective = getEffectivePrice(item);
              const hasContract = customPrices[item.id] !== undefined;
              return (
                <Card key={item.id} className="border-2" data-testid={`cart-item-${item.id}`}>
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-24 h-24 object-cover rounded-lg bg-slate-100"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-slate-800 mb-2">{item.name}</h3>
                        <div className="mb-2 flex items-center gap-2 flex-wrap">
                          {hasContract ? (
                            <>
                              <span className="text-emerald-700 font-semibold">${effective.toFixed(2)} each</span>
                              <span className="text-xs text-slate-400 line-through">${Number(item.price).toFixed(2)}</span>
                              <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                                Contract
                              </span>
                            </>
                          ) : (
                            <span className="text-slate-600">${Number(item.price).toFixed(2)} each</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center border-2 border-slate-300 rounded-lg">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="px-4 font-semibold">{item.quantity}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              disabled={item.quantity >= item.stock}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-blue-600" data-testid={`cart-line-total-${item.id}`}>
                          ${(effective * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div>
            <Card className="border-2 sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-6 text-slate-800">Order Summary</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span className="font-semibold" data-testid="cart-subtotal">${getCartTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Shipping</span>
                    <span className="font-semibold">Calculated at checkout</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-xl font-bold text-slate-800">
                    <span>Total</span>
                    <span className="text-blue-600" data-testid="cart-total">${getCartTotal().toFixed(2)}</span>
                  </div>
                </div>
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white mb-3"
                  size="lg"
                  onClick={() => navigate('/checkout')}
                  data-testid="cart-proceed-checkout"
                >
                  Proceed to Checkout
                </Button>
                <Link to="/products">
                  <Button variant="outline" className="w-full">
                    Continue Shopping
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
